import { useState, useEffect } from "react";
import withHydrationOnDemand from "react-hydration-on-demand";
import { A11y, Autoplay, Navigation, Pagination } from "swiper";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";
import ImageWrapper from "../../global/components/ImageWrapper/ImageWrapper";
import { useImageSize } from "../../global/components/ImageWrapper/ImageWrapper.methods";
import LinkWrapper from "../../global/components/LinkWrapper/LinkWrapper";
import { isPortalSide } from "../../helpers/helpers";
import { useLinkContentActions } from "../../shared/hooks/useLinkContentAction";
import StyledText from "../../shared/styleElements/StyledText/StyledText";
import { useBoxTilesSlider } from "./BoxTilesSlider.methods";
import {
  IBoxTilesSliderProps,
  TilesSliderRotateOptions,
} from "./BoxTilesSlider.types";
import {
  StyledSwiperSlideContainer,
  StyledTilesSlider,
} from "./StyledTilesSlider";
import { Memo } from "../../helpers/memoWrapper";
import PageInPageComponent from "../../structure/PageInPage/PageInPageComponent";
import InnerBox from "@ecp-boxes/shared/components/InnerBox/InnerBox";

const BoxTilesSlider: React.FC<IBoxTilesSliderProps> = (props) => {
  const { getHref, linkContentActions, popupRef } =
    useLinkContentActions<HTMLDivElement>(props.path);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [imgWrapper, { width: imageWidth }] = useImageSize();

  const {
    content,
    contentSettings,
    displaySettings,
    getSwiperCustomConfiguration,
  } = useBoxTilesSlider(props);

  const currentTileIndex = content?.tiles?.findIndex(
    (tile) => tile.id + props.id === props.pathExtraSettings
  );

  useEffect(() => {
    if (swiper != null && !isPortalSide()) {
      swiper.slideTo(currentTileIndex, 0);
    }
  }, [swiper, currentTileIndex]);

  if (!isPortalSide()) {
    displaySettings.slideRotateType = TilesSliderRotateOptions.SINGLE_TILE;
  }

  return (
    <StyledTilesSlider
      $contentSettings={contentSettings}
      $displaySettings={displaySettings}
      data-testid="boxTilesSlider"
      className="tiles-container tiles-slider-custom-style"
      ref={popupRef}
    >
      <StyledText
        $settings={{
          text: {
            color: contentSettings.headerTextColor,
          },
          font: contentSettings.headerTextStyle,
        }}
        className="tiles-container__title"
      >
        {content.header}
      </StyledText>

      <Swiper
        className="swiper"
        modules={[Navigation, A11y, Autoplay, Pagination]}
        spaceBetween={contentSettings.spaceBetweenTiles}
        onSwiper={setSwiper}
        {...getSwiperCustomConfiguration()}
      >
        {props.settings.enable_page_slider === "false"
          ? contentSettings.tileSettings?.map((contentSettingsTile, index) => {
              const currentTileContent = content.tiles[index];
              const href = getHref(currentTileContent.slideUrl);

              return (
                <SwiperSlide
                  key={content.tiles[index].id}
                  data-testid="slider-slide"
                  className="swiper__slide"
                >
                  <LinkWrapper
                    className="swiper__slide__link"
                    openInNewTab={
                      currentTileContent.slideUrl.goToPageSetting?.openInNewTab
                    }
                    onClick={() =>
                      linkContentActions(currentTileContent.slideUrl)
                    }
                    href={href ?? ""}
                  >
                    <div style={{ width: "100%", height: "100%" }}>
                      <StyledSwiperSlideContainer
                        ref={imgWrapper}
                        tileSettings={contentSettingsTile}
                      >
                        <>
                          {contentSettingsTile.show.title &&
                            (href !== undefined ? (
                              <LinkWrapper
                                className="swiper__slide__title"
                                linkStyle={content.tiles[index].linkStyle}
                                fontStyle={contentSettingsTile.titleStyle}
                                basicColor={contentSettingsTile.titleColor}
                                onClick={() =>
                                  linkContentActions(
                                    currentTileContent.slideUrl
                                  )
                                }
                                href={href}
                              >
                                {content.tiles[index].title}
                              </LinkWrapper>
                            ) : (
                              <StyledText
                                $settings={{
                                  text: {
                                    color: contentSettingsTile.titleColor,
                                  },
                                  font: contentSettingsTile.titleStyle,
                                }}
                                className="swiper__slide__title"
                              >
                                {content.tiles[index].title}
                              </StyledText>
                            ))}

                          {contentSettingsTile.show.image && imageWidth && (
                            <div className="swiper__slide__image-container">
                              <div>
                                <ImageWrapper
                                  imageUrl={content.tiles[index].image.imageUrl}
                                  seoDescription={
                                    content.tiles[index].seoDescription
                                  }
                                  imageFit={!contentSettingsTile.fill}
                                  imageAlignment={
                                    contentSettingsTile.image.alignment
                                  }
                                  opacity={contentSettingsTile.image.opacity}
                                  width={imageWidth}
                                  imgTagAsBackground
                                />
                              </div>
                            </div>
                          )}
                          <div className="swiper__slide__inner-box-container">
                            <InnerBox
                              content={{
                                ...contentSettingsTile,
                                secondaryButton: {
                                  ...contentSettingsTile.secondButton,
                                },
                              }}
                              settings={{
                                box: {
                                  ...contentSettingsTile.box,
                                  textPadding: contentSettingsTile.box.padding,
                                  color:
                                    contentSettingsTile.box.backgroundColor,
                                  alignment: {
                                    horizontal:
                                      contentSettingsTile.box
                                        .boxHorizontalAlignment,
                                    vertical:
                                      contentSettingsTile.box
                                        .boxVerticalAlignment,
                                  },
                                },
                                padding: displaySettings.padding,
                                title: {
                                  ...contentSettingsTile.boxTitle,
                                  font: contentSettingsTile.boxTitle.style,
                                  text: {
                                    color: contentSettingsTile.boxTitle.color,
                                  },
                                },
                                description: {
                                  ...contentSettingsTile.description,
                                  font: contentSettingsTile.description.style,
                                  text: {
                                    color:
                                      contentSettingsTile.description.color,
                                  },
                                },
                                mainButton: {
                                  ...contentSettingsTile.mainButton.style,
                                  show: contentSettingsTile.mainButton.show,
                                },
                                secondaryButton: {
                                  ...contentSettingsTile.secondButton.style,
                                  show: contentSettingsTile.secondButton.show,
                                },
                              }}
                              messages={{
                                box_content_description:
                                  contentSettingsTile.description.heading,
                                box_content_title:
                                  contentSettingsTile.boxTitle.heading,
                                box_content_main_button_heading:
                                  contentSettingsTile.mainButton.label,
                                box_content_secondary_button_heading:
                                  contentSettingsTile.secondButton.label,
                              }}
                              className="slider-container__slide-inner-box"
                            />
                          </div>
                        </>
                      </StyledSwiperSlideContainer>
                    </div>
                  </LinkWrapper>
                </SwiperSlide>
              );
            })
          : ([0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map((index) => {
              const url = props.settings[`slide_${index}`];
              if (!url) return null;
              return (
                <SwiperSlide
                  key={index}
                  data-testid="slider-slide"
                  className="swiper__slide"
                >
                  <PageInPageComponent url={url} />
                </SwiperSlide>
              );
            })}
      </Swiper>
    </StyledTilesSlider>
  );
};

export default Memo(withHydrationOnDemand({ on: ["visible"] })(BoxTilesSlider));
